import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaPaperPlane, FaRobot } from "react-icons/fa";
import robotIcon from "../../assets/robot.png";
import { ThreeDots } from "react-loader-spinner";

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Hello! I'm your Elite Body Home assistant. May I have your phone number to assist you better?",
      sender: "bot",
      timestamp: new Date(),
    },
  ]);
  const [newMessage, setNewMessage] = useState("");
  const [showWelcome, setShowWelcome] = useState(true);
  const [isTyping, setIsTyping] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const messagesEndRef = useRef(null);

  // Query the backend API with proper payload
  const queryAI = async (messageText) => {
    try {
      setIsTyping(true);

      const payload = {
        question: messageText,
        sessionId: phoneNumber, // Use phone number or "guest" if not provided
        chatId: phoneNumber, // Same as sessionId
        memoryType: "Buffer Window Memory",
      };

      const response = await fetch(
        "https://ai.elitebodyhome.com/api/v1/prediction/cd5097d9-82f4-4e2f-8a33-9c1e56cfafdb",
        {
          headers: {
            Authorization: "Bearer tnCiVrQJk4mNGiKUNo9B8T5Rd30NEjDejtRzGyBi6DE",
            "Content-Type": "application/json",
          },
          method: "POST",
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();
      return (
        result.text ||
        "Sorry, I didn't understand that. Could you please rephrase?"
      );
    } catch (error) {
      console.error("Error querying AI:", error);
      return "Sorry, I'm having trouble connecting. Please try again later.";
    } finally {
      setIsTyping(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initial state - don't show immediately
    setShowWelcome(false);

    // Show after 1 second
    const showTimer = setTimeout(() => {
      setShowWelcome(true);
    }, 3000);

    // Hide after 16 seconds (1s delay + 15s display)
    const hideTimer = setTimeout(() => {
      setShowWelcome(false);
    }, 16000);

    // Cleanup both timers
    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, []);
  const handleSendMessage = async () => {
    if (newMessage.trim() === "") return;

    // Phone number validation (first message)
    if (!phoneNumber && messages.length === 1) {
      // Updated regex to allow optional + prefix
      const phoneRegex = /^\+?[0-9]{10,15}$/;
      const inputNumber = newMessage.trim();

      if (phoneRegex.test(inputNumber)) {
        // Normalize phone number (remove + if present)
        const normalizedNumber = inputNumber.startsWith("+")
          ? inputNumber
          : `+${inputNumber}`;

        setPhoneNumber(normalizedNumber);

        // Add user message (phone number)
        const userMsg = {
          text: inputNumber, // Show exactly what user entered
          sender: "user",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMsg]);
        setNewMessage("");

        // Send confirmation message
        const botMsg = {
          text: "Thank you! How can I assist you today?",
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMsg]);
        return;
      } else {
        const botMsg = {
          text: "Please enter a valid phone number with country code",
          sender: "bot",
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMsg]);
        setNewMessage("");
        return;
      }
    }

    const userMsg = {
      text: newMessage,
      sender: "user",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setNewMessage("");

    // Get AI response
    const aiResponse = await queryAI(newMessage);
    const botMsg = {
      text: aiResponse,
      sender: "bot",
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, botMsg]);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: 20,
        right: 20,
        zIndex: 100,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
      }}
    >
      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.8 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.8 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            style={{
              maxWidth: 380,
              maxHeight: "70vh",
              backgroundColor: "white",
              borderRadius: 12,
              boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              marginBottom: 10,
            }}
          >
            {/* Chat Header */}
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: "#c69933",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap", // Responsive wrap
                gap: "8px",
              }}
            >
              {/* Left section */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  minWidth: "200px",
                  flex: "1",
                }}
              >
                <img
                  src={robotIcon}
                  alt="Assistant"
                  style={{ width: 32, height: 32 }}
                />
                <div>
                  <div style={{ fontWeight: "bold" }}>EBH Support Center</div>
                  <small style={{ fontSize: "0.75rem" }}>
                    Typically replies within 30 secs
                  </small>
                </div>
              </div>

              {/* Right side button */}
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "1.2rem",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
                aria-label="Close chat"
              >
                <FaTimes />
              </button>
            </div>

            {/* Messages Container */}
            <div
              style={{
                flex: 1,
                padding: "12px 16px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: 12,
                wordBreak: "break-word",
                overflowX: "hidden",
              }}
            >
              {messages.map((message, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    alignSelf:
                      message.sender === "user" ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px",
                      borderRadius:
                        message.sender === "user"
                          ? "12px 12px 0 12px"
                          : "12px 12px 12px 0",
                      backgroundColor:
                        message.sender === "user" ? "#c69933" : "#f0f0f0",
                      color: message.sender === "user" ? "white" : "black",
                    }}
                  >
                    {message.text}
                  </div>
                  <div
                    style={{
                      fontSize: 10,
                      color: "#888",
                      alignSelf:
                        message.sender === "user" ? "flex-end" : "flex-start",
                    }}
                  >
                    {formatTime(new Date(message.timestamp))}
                  </div>
                </motion.div>
              ))}
              {isTyping && (
                <div
                  style={{
                    alignSelf: "flex-start",
                    padding: "8px 10px",
                    borderRadius: "12px 12px 12px 0",
                    backgroundColor: "#f0f0f0",
                  }}
                >
                  <ThreeDots
                    height={12}
                    width={32}
                    radius={2}
                    color="#666"
                    ariaLabel="loading"
                  />
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div
              style={{
                padding: 12,
                borderTop: "1px solid #eee",
                display: "flex",
                gap: 8,
                flexShrink: 0,
                backgroundColor: "#f9f9f9",
              }}
            >
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={
                  !phoneNumber && messages.length === 1
                    ? "Please enter your phone number"
                    : "Type your message..."
                }
                style={{
                  flex: 1,
                  padding: "10px 14px",
                  border: "1px solid #ddd",
                  borderRadius: 20,
                  outline: "none",
                  resize: "none",
                  maxHeight: 120,
                  overflowY: "auto",
                  wordBreak: "break-word",
                  fontFamily: "inherit",
                  fontSize: 14,
                }}
                rows={1}
              />
              <button
                onClick={handleSendMessage}
                disabled={newMessage.trim() === "" || isTyping}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  backgroundColor:
                    newMessage.trim() === "" ? "#ccc" : "#c69933",
                  color: "white",
                  border: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: newMessage.trim() === "" ? "not-allowed" : "pointer",
                  flexShrink: 0,
                  transition: "background-color 0.2s",
                }}
              >
                <FaPaperPlane />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Button with Welcome Message */}
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <AnimatePresence>
          {showWelcome && !isOpen && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              style={{
                padding: "12px 16px",
                backgroundColor: "white",
                borderRadius: 12,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                maxWidth: 240,
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                }}
              >
                <FaRobot color="#c69933" />
                <span
                  style={{
                    fontWeight: "bold",
                    color: "#c69933",
                    fontSize: 14,
                  }}
                >
                  Hi there! 👋
                </span>
              </div>
              <div
                style={{
                  fontSize: 12,
                  lineHeight: 1.4,
                }}
              >
                Welcome to Elite Body Home. I'm here to assist you today!
              </div>
              <button
                onClick={() => setShowWelcome(false)}
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "none",
                  border: "none",
                  color: "#999",
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                <FaTimes />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          onClick={() => {
            setIsOpen(!isOpen);
            setShowWelcome(false);
          }}
          aria-label="Chat with us"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          animate={{
            y: [0, -5, 5, -5, 5, 0],
            rotate: [0, -5, 5, -5, 5, 0],
          }}
          transition={{
            repeat: Infinity,
            repeatDelay: 3,
            duration: 1.5,
          }}
        >
          <img
            src={robotIcon}
            alt="Chat assistant"
            style={{ width: 60, height: 60 }}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default ChatBot;
